<template>
  <div>
    <div
      v-if="showSpanishModal && false"
      class="p-4 mb-8 bg-white rounded-lg animation-scaling animation-once animation-ease-in-out animation-1/2s"
    >
      <!-- Temporary modal until service gets organize setting Spanish classes in the admin portal -->
      <div class="p-4 font-semibold leading-relaxed rounded bg-ricoma-gray-200">
        ¿Buscas clases en español? Envíanos un email a
        <a href="mailto:service@ricoma.com" class="underline"> service@ricoma.com </a>
        con la solicitud.
      </div>
    </div>

    <div class="relative pt-4 bg-white rounded-lg">
      <h4 class="text-xl font-semibold text-center">{{ $t('schedule.modalNew.heading') }}</h4>
      <span
        class="absolute top-0 right-0 block p-1 mt-4 ml-auto mr-4 rounded-full cursor-pointer bg-ricoma-gray-300 text-ricoma-gray-500"
        @click="$emit('close')"
      >
        <VSvgIcon name="close" />
      </span>

      <form action="#" class="form" @submit.prevent="handleSubmit">
        <div class="form-fields">
          <VFormSelect
            :label="`${$t('schedule.modalNew.form.fields.f1.label')}`"
            :placeholder="`${$t('schedule.modalNew.form.fields.f1.placeholder')}`"
            :options="machinesOptions"
            @onSelect="selection => handleSelect('machine')(selection)"
          />

          <VFormInput
            :label="`${$t('schedule.modalNew.form.fields.f2.label')}`"
            :placeholder="`${$t('schedule.modalNew.form.fields.f2.placeholder')}`"
            :onlyDigits="true"
            :error="errors['phone']"
            :formatValue="formatPhoneNumOnChange"
            @update="handleInput"
          />

          <VFormSelect
            :label="`${$t('schedule.modalNew.form.fields.f3.label')}`"
            :placeholder="`${$t('schedule.modalNew.form.fields.f3.placeholder')}`"
            :options="trainingTypesOptions"
            @onSelect="selection => handleSelect('type')(selection)"
          />

          <VFormSelect
            :label="`${$t('schedule.modalNew.form.fields.f4.label')}`"
            :placeholder="`${$t('schedule.modalNew.form.fields.f4.placeholder')}`"
            :options="languagesOptions"
            @onSelect="selection => handleSelect('language')(selection)"
          />

          <VFormSelect
            :label="`${$t('schedule.modalNew.form.fields.f5.label')}`"
            :placeholder="`${$t('schedule.modalNew.form.fields.f5.placeholder')}`"
            :options="timesOptions"
            :optionKeys="['time', 'training_topic']"
            :formatOptions="handleFormatOptions"
            :formatSelection="handleFormatSelection"
            :asHtml="true"
            :fullDisabled="isTimeFieldDisabled"
            @onSelect="handleSelectTime"
          />
        </div>
        <button :disabled="!next" class="max-w-full py-6 rounded-t-none rounded-b-lg button">
          {{ $t('schedule.modalNew.form.btn') }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { parseISO, format } from 'date-fns';

import { formatPhoneNumOnChange, isPhoneNum } from '@/utils';

import VFormSelect from '@/components/base/VFormSelect';
import VFormInput from '@/components/base/VFormInput';
import VSvgIcon from '@/components/base/VSvgIcon';

export default {
  name: 'ScheduleForm',
  components: { VFormSelect, VFormInput, VSvgIcon },
  props: { excludedTimes: Array },
  data() {
    return {
      machines: null,
      trainingTypes: null,
      languages: null,
      times: null,
      fieldsData: {
        machine: null,
        type: null,
        language: null,
        phone: null,
      },
      errors: {
        phone: '',
      },
      timesOptions: [],
      time: null,
    };
  },
  computed: {
    showSpanishModal() {
      return this.fieldsData.language === 'Spanish';
    },
    machinesOptions() {
      return (
        this.machines &&
        this.machines.map(m => (m.categories ? `${m.name} (${m.categories.name})` : m.name))
      );
    },
    trainingTypesOptions() {
      return (
        this.trainingTypes &&
        Object.values(this.trainingTypes).reduce((acc, type) => acc.concat(type), [])
      );
    },
    languagesOptions() {
      return (
        this.languages && Object.values(this.languages).reduce((acc, lang) => acc.concat(lang), [])
      );
    },
    next() {
      const { machine, type, language, phone } = this.fieldsData;
      const time = this.time;
      const phoneError = this.errors.phone;
      return machine && language && type && phone && time && !phoneError;
    },
    isTimeFieldDisabled() {
      const { machine, type, language, phone } = this.fieldsData;
      return !machine || !type || !language || !phone;
    },
  },
  watch: {
    async fieldsData(data) {
      let filtered = [];
      const { machine, type, language, phone } = data;

      // we need all four fields before checking for available times
      const condition = machine && language && type && phone;

      if (condition) {
        this.times &&
          machine &&
          (filtered = await this.filterTimes(this.times, machine, 'machine'));
        this.times && type && (filtered = await this.filterTimes(filtered, type, 'type'));
        this.times &&
          language &&
          (filtered = await this.filterTimes(filtered, language, 'language'));
        this.timesOptions = filtered
          ? filtered
              .filter(t => !this.excludedTimes.includes(t.id))
              .filter(
                t =>
                  new Date(t.time).toLocaleString('en-us', { weekday: 'long' }) !== 'Saturday' &&
                  new Date(t.time).toLocaleString('en-us', { weekday: 'long' }) !== 'Sunday',
              )
          : [];
      } else {
        this.timesOptions = [];
      }
    },
  },
  methods: {
    handleFormatSelection(selection) {
      return this.formatDateTime(selection.time);
    },
    handleFormatOptions(option) {
      return Object.entries(option).reduce((acc, [key, val]) => {
        val
          ? (acc +=
              key === 'time'
                ? `<div>${this.formatDateTime(val)}</div>`
                : `<div class='text-sm italic leading-loose text-gray-700'>- ${val}</div>`)
          : acc;
        return acc;
      }, '');
    },
    handleSetError(field, message) {
      this.errors = { ...this.errors, [field]: message };
    },
    formatPhoneNumOnChange(phone) {
      return formatPhoneNumOnChange(phone);
    },
    handleInput(phone) {
      !isPhoneNum(phone)
        ? this.handleSetError('phone', 'Invalid phone number')
        : this.handleSetError('phone', '');

      this.fieldsData = { ...this.fieldsData, phone };
    },
    handleSelect(name) {
      return selection => {
        this.fieldsData = { ...this.fieldsData, [name]: selection };
      };
    },
    handleSelectTime(time) {
      this.time = time;
    },
    handleSubmit() {
      const { machine, phone } = this.fieldsData;
      const slotId = this.time.id;
      const groupId = this.time.product_groups.find(
        product => product.name === machine.replace(`(${product.category_name})`, '').trim(),
      ).id;
      const time = this.time.time;
      this.$emit('next', { slotId, groupId, phone, time });
    },
    async filterTimes(data, filter, filterBy) {
      switch (filterBy) {
        case 'machine': {
          const { data: timesData } = await this.$http.get('/training/time-slots', {
            params: {
              from: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
              //from: format(addDays(new Date(), 1), 'yyyy-MM-dd HH:mm:ss'),
              group: filter,
            },
          });
          return timesData.data.filter(time =>
            time.product_groups.some(
              product => product.name === filter.replace(`(${product.category_name})`, '').trim(),
            ),
          );
        }

        case 'type':
          return data.filter(time => time.type_name === filter);
        case 'language':
          return data.filter(time => time.lang_name === filter);
        default:
          return data;
      }
    },
    mapTimesToFormattedDate(timesArr) {
      return timesArr.reduce((times, time) => {
        const formattedTime = this.formatDateTime(time.time);
        times[formattedTime] = time;
        return times;
      }, {});
    },
    formatDateTime(d) {
      const date = parseISO(d);
      return format(date, "PPPP 'at' h:mm aaaa '( EASTERN TIME )'");
    },
  },
  async created() {
    const { data: groupsData } = await this.$http.get('/training/groups');
    const { data: typesData } = await this.$http.get('/training/types');
    const { data: langsData } = await this.$http.get('/training/langs');
    const { data: timesData } = await this.$http.get('/training/time-slots', {
      params: {
        from: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        //from: format(addDays(new Date(), 1), 'yyyy-MM-dd HH:mm:ss'),
      },
    });

    // fields
    this.machines = groupsData.data;
    this.trainingTypes = typesData.data;
    this.languages = langsData.data;
    this.times = timesData.data;
  },
};
</script>
